import { CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { Badge, Tooltip } from "@radix-ui/themes";

import {
  deriveVersionApproval,
  describeApproval,
  type VersionApproval,
  type VersionReviewLike,
} from "../utils/versionApproval";

/**
 * Presentational approval chip for an ALREADY-DERIVED decision. Shared between
 * the review header badge (which derives from raw `reviews[]`) and the project
 * Reviews rail card (which receives the server-derived decision). Display-only:
 * the Ask Puntt agent owns the state (rembrandt `SetVersionReviewStatusTool`).
 */
export function VersionApprovalBadgeView({
  approval,
  className,
}: {
  approval: VersionApproval;
  className?: string;
}) {
  const approved = approval.decision === "approved";
  const description = describeApproval(approval);

  return (
    <Tooltip content={description}>
      <Badge
        size="1"
        variant="soft"
        color={approved ? "green" : "amber"}
        className={className}
        data-testid="version-approval-badge"
        aria-label={description}
      >
        {approved ? (
          <CheckCircle
            size={12}
            weight="fill"
            aria-hidden="true"
            data-testid="version-approval-badge-icon"
          />
        ) : (
          <PencilSimple
            size={12}
            weight="fill"
            aria-hidden="true"
            data-testid="version-approval-badge-icon"
          />
        )}
        {approved ? "Approved" : "Changes requested"}
      </Badge>
    </Tooltip>
  );
}

/**
 * Small, display-only indicator of a version's human approval decision in the
 * shared project review header. The Ask Puntt agent owns the state — it records
 * "approved" / "changes requested" from the chat (rembrandt
 * `SetVersionReviewStatusTool`); this badge never mutates anything. WHO/WHEN
 * come straight from the version's `reviews[]` records.
 */
export function VersionApprovalBadge({
  reviews,
  className,
}: {
  reviews: readonly VersionReviewLike[] | null | undefined;
  className?: string;
}) {
  const approval = deriveVersionApproval(reviews);
  if (!approval) return null;

  return <VersionApprovalBadgeView approval={approval} className={className} />;
}
